import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

import {
  AgentThreadGoalRecord,
  AgentThreadGoalStatus,
} from "@/shared/domain/agentThread/AgentThreadRecord";

import styles from "./GoalsStrip.module.css";

interface GoalsStripProps {
  goals: AgentThreadGoalRecord[];
}

// User-facing labels for known goal types. Until the API exposes a handler-supplied
// display name, this small mapping is a deliberate stopgap — the goal_type discriminator
// is stable wire-format that won't change without coordination, and only two types
// exist today. Add an entry here when a new goal type ships.
const GOAL_TYPE_LABELS: Record<string, string> = {
  dataset_summary: "Generate dataset summary",
  dataset_triage: "Triage dataset",
};

function labelForGoal(goal: AgentThreadGoalRecord): string {
  const known = GOAL_TYPE_LABELS[goal.goal_type];
  if (known) {
    return known;
  }
  // Humanize unknown snake_case discriminators so a freshly-shipped goal type
  // surfaces as "New Custom Goal" rather than "new_custom_goal".
  return goal.goal_type
    .split("_")
    .map((part) =>
      part.length > 0 ? part[0].toUpperCase() + part.slice(1) : part,
    )
    .join(" ");
}

function chipClassNameForStatus(status: AgentThreadGoalStatus): string {
  switch (status) {
    case AgentThreadGoalStatus.ACHIEVED:
      return `${styles.chip} ${styles.chipAchieved}`;
    case AgentThreadGoalStatus.FAILED:
      return `${styles.chip} ${styles.chipFailed}`;
    case AgentThreadGoalStatus.PENDING:
    default:
      return `${styles.chip} ${styles.chipPending}`;
  }
}

function iconForStatus(status: AgentThreadGoalStatus): React.ReactElement {
  switch (status) {
    case AgentThreadGoalStatus.ACHIEVED:
      return <CheckCircleOutlineIcon className={styles.icon} />;
    case AgentThreadGoalStatus.FAILED:
      return <ErrorOutlineIcon className={styles.icon} />;
    case AgentThreadGoalStatus.PENDING:
    default:
      return <HourglassEmptyIcon className={styles.icon} />;
  }
}

/**
 * Renders the goals declared on a single turn as a horizontal strip of status
 * chips, one per goal. Surfaces what each turn was asked to achieve and
 * whether the agent succeeded.
 */
export function GoalsStrip({ goals }: GoalsStripProps) {
  if (goals.length === 0) {
    return null;
  }

  return (
    <div className={styles.container} aria-label="Declared goals for this turn">
      {goals.map((goal, idx) => (
        <span
          key={`${goal.message_sequence_num}-${idx}`}
          className={chipClassNameForStatus(goal.status)}
          title={`${labelForGoal(goal)}: ${goal.status}`}
        >
          {iconForStatus(goal.status)}
          <span className={styles.label}>{labelForGoal(goal)}</span>
        </span>
      ))}
    </div>
  );
}
